import {
  StudentLimitedResponse,
  StudentSubjectsService,
  OrgSubjectsService,
  StudentSubjectPublicResponse,
  OrganizationSubjectPublicResponse,
  Auth0AccountRole,
} from "client/openapi";
import { useState, useEffect, useContext, useCallback } from "react";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { Select } from "components/Select";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";
import { ReaderIcon } from "@radix-ui/react-icons";

export default function Subjects({
  student,
  organization_id,
}: {
  student: StudentLimitedResponse;
  organization_id: number;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const [studentSubjects, setStudentSubjects] = useState<
    StudentSubjectPublicResponse[]
  >([]);
  const [orgSubjects, setOrgSubjects] = useState<
    OrganizationSubjectPublicResponse[]
  >([]);
  const [availableSubjects, setAvailableSubjects] = useState<
    OrganizationSubjectPublicResponse[]
  >([]);
  const [addingSubject, setAddingSubject] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [deleteIntentKey, setDeleteIntentKey] = useState<number>();

  // Get all student subjects for this student
  const getStudentSubjects = useCallback(async () => {
    StudentSubjectsService.getStudentSubjectsByStudentId({
      studentId: student.id,
      active: true,
    })
      .then((subjects) => {
        const activeSubjects = subjects.filter((subject) => subject.active);
        setStudentSubjects(activeSubjects);
      })
      .catch((error) => {
        console.error("Error fetching student subjects:", error);
        Notifications.error("Unable to load student subjects");
      });
  }, [student.id]);

  // Get all organization subjects
  const getOrgSubjects = useCallback(async () => {
    OrgSubjectsService.getSubjectsByOrg({
      orgId: organization_id,
      active: true,
    })
      .then((subjects) => {
        setOrgSubjects(subjects as OrganizationSubjectPublicResponse[]);
      })
      .catch((error) => {
        console.error("Error fetching organization subjects:", error);
        Notifications.error("Unable to load organization subjects");
      });
  }, [organization_id]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([getStudentSubjects(), getOrgSubjects()]);
      setLoading(false);
    };
    loadData();
  }, [student.id, organization_id, getStudentSubjects, getOrgSubjects]);

  // Update available subjects when org subjects or student subjects change
  useEffect(() => {
    if (orgSubjects.length === 0) {
      setAvailableSubjects([]);
      return;
    }

    const available = orgSubjects.filter(
      (orgSubject) =>
        !studentSubjects.some(
          (studentSubject) => studentSubject.org_subject.id === orgSubject.id
        )
    );
    setAvailableSubjects(available);
  }, [orgSubjects, studentSubjects]);

  // Handle adding a subject to the student
  const handleAddSubject = async (
    orgSubject: OrganizationSubjectPublicResponse
  ) => {
    StudentSubjectsService.toggleStudentSubjectActive({
      studentId: student.id,
      orgSubjectId: orgSubject.id,
      active: true,
    })
      .then((newStudentSubject) => {
        // Update local state directly
        setStudentSubjects((prev) => [...prev, newStudentSubject]);
        setAvailableSubjects((prev) =>
          prev.filter((subject) => subject.id !== orgSubject.id)
        );
        Notifications.success(
          `${orgSubject.name} successfully added to ${student.first_name}`
        );
        setAddingSubject(false);
      })
      .catch((error) => {
        console.error("Error adding subject:", error);
        Notifications.error(
          `Unable to add ${orgSubject.name} to ${student.first_name}`
        );
      });
  };

  // Handle removing a subject from the student (toggle active to false)
  const handleRemoveSubject = async (
    studentSubject: StudentSubjectPublicResponse
  ) => {
    StudentSubjectsService.toggleStudentSubjectActive({
      studentId: student.id,
      orgSubjectId: studentSubject.org_subject.id,
      active: false,
    })
      .then(() => {
        // Update local state directly
        setStudentSubjects((prev) =>
          prev.filter((subject) => subject.id !== studentSubject.id)
        );
        setAvailableSubjects((prev) => [...prev, studentSubject.org_subject]);
        Notifications.success(
          `${studentSubject.org_subject.name} is no longer linked to ${student.first_name}`
        );
        setDeleteIntentKey(-1);
      })
      .catch((error) => {
        console.error("Error removing subject:", error);
        Notifications.error(
          `Unable to remove ${studentSubject.org_subject.name} from ${student.first_name}`
        );
      });
  };

  if (loading) {
    return (
      <section className="student-profile--section" id="student-subjects">
        <div className="student-profile--section-header">
          <SectionTitle icon={<ReaderIcon />} tone={BubbleTone.TEAL}>
            Subjects
          </SectionTitle>
        </div>
        <div className="parent-profile--section-items">
          <div className="parent-profile--section-item">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="student-profile--section" id="student-subjects">
      <div className="student-profile--section-header">
        <SectionTitle icon={<ReaderIcon />} tone={BubbleTone.TEAL}>
          Subjects
        </SectionTitle>

        {addingSubject ? (
          <Select
            id="add-subject"
            name="add-subject"
            options={availableSubjects}
            value={undefined}
            isDisabled={availableSubjects?.length === 0}
            getOptionLabel={(s) => s.name}
            getOptionValue={(s) => s.id.toString()}
            onChange={(val) => {
              handleAddSubject(val as OrganizationSubjectPublicResponse);
            }}
            className="w-36 h-9 text-sm"
            placeholder="Select subject..."
          />
        ) : (
          <div className="flex flex-row gap-1.5 items-center">
            {[Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
              currently_selected_role as Auth0AccountRole
            ) && (
              <Button
                color={ButtonColor.SKYBLUE}
                size={ButtonSize.SMALL}
                onClick={() => setAddingSubject(true)}
                disabled={availableSubjects.length === 0}
              >
                Add Subject
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="student-profile--section-items max-h-[380px] overflow-y-auto">
        {studentSubjects.length > 0 ? (
          studentSubjects.map((studentSubject, i) => (
            <div
              key={i}
              className="student-profile--section-item parent-detail--student"
            >
              <div className="parent-detail--student-details">
                <p className="font-medium">{studentSubject.org_subject.name}</p>
                {studentSubject.org_subject.description && (
                  <p className="text-sm text-gray-600">
                    {studentSubject.org_subject.description}
                  </p>
                )}
                {[
                  Auth0AccountRole.ORG_ADMIN,
                  Auth0AccountRole.ORG_TUTOR,
                ].includes(currently_selected_role as Auth0AccountRole) && (
                  <div className="mt-2">
                    {deleteIntentKey === studentSubject.id ? (
                      <>
                        <span style={{ marginRight: "5px" }}>
                          <Button
                            onClick={() => {
                              handleRemoveSubject(studentSubject);
                            }}
                            color={ButtonColor.RED}
                            size={ButtonSize.EXTRA_SMALL}
                          >
                            confirm removal
                          </Button>
                        </span>
                        <span>
                          <Button
                            onClick={() => setDeleteIntentKey(-1)}
                            color={ButtonColor.SKYBLUE}
                            size={ButtonSize.EXTRA_SMALL}
                          >
                            cancel
                          </Button>
                        </span>
                      </>
                    ) : (
                      <span>
                        <Button
                          color={ButtonColor.SKYBLUE}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.EXTRA_SMALL}
                          onClick={() => setDeleteIntentKey(studentSubject.id)}
                        >
                          remove
                        </Button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="student-profile--section-item">
            No subjects assigned.
          </div>
        )}
      </div>
    </section>
  );
}
